import { useEffect, useMemo, useState } from 'react';
import AdminLayout from '../components/AdminLayout';
import { useTheme } from '../context/ThemeContext';
import { fetchContactSubmissions, updateContactSubmission } from '@/lib/api/forms';
import type { ContactSubmissionRecord, ContactSubmissionStatus } from '@/lib/domain';

const STATUS_META: Record<ContactSubmissionStatus, { label: string; pill: string; icon: string }> = {
  new: { label: 'New', pill: 'bg-amber-500/20 text-amber-400', icon: 'ri-mail-unread-line' },
  in_progress: { label: 'In Progress', pill: 'bg-sky-500/20 text-sky-400', icon: 'ri-loader-4-line' },
  replied: { label: 'Replied', pill: 'bg-emerald-500/20 text-emerald-400', icon: 'ri-mail-send-line' },
  archived: { label: 'Archived', pill: 'bg-white/10 text-white/60', icon: 'ri-archive-line' },
};

type EmailTemplateKey = 'received' | 'follow_up' | 'dealer_interest' | 'product_help' | 'custom';

function buildEmailTemplate(
  key: EmailTemplateKey,
  submission: ContactSubmissionRecord,
): { subject: string; body: string } {
  const greeting = `Hi ${submission.firstName},`;
  const signature = '\n\nBest regards,\nStingray Support Team\ninfo@stingrayenergy.net\n+1 307 381 5172';
  const safeSubject = submission.subject || 'your inquiry';

  const templates: Record<EmailTemplateKey, { subject: string; body: string }> = {
    received: {
      subject: `Re: ${safeSubject}`,
      body: `${greeting}\n\nThanks for reaching out to Stingray. We received your message and our team is reviewing it now.\n\nWe'll follow up with a more detailed reply as soon as possible.${signature}`,
    },
    follow_up: {
      subject: `Re: ${safeSubject} — follow-up`,
      body: `${greeting}\n\nThanks for your message. We are reviewing the details with our team and may need a bit more time before we send the final answer.\n\nIf you have any photos, order details, or product references that might help, feel free to reply with them.${signature}`,
    },
    dealer_interest: {
      subject: `Re: ${safeSubject} — dealer inquiry`,
      body: `${greeting}\n\nThank you for your interest in working with Stingray.\n\nTo help us review your request faster, please reply with your company name, market/region, and the product lines you are most interested in carrying.\n\nOnce we have that, our team can guide you through the next steps.${signature}`,
    },
    product_help: {
      subject: `Re: ${safeSubject} — product guidance`,
      body: `${greeting}\n\nThank you for contacting Stingray.\n\nWe’d be happy to help recommend the right battery setup for your application. If you can share your vessel type, current battery bank, charger/inverter details, and the products you want to run, we can point you in the right direction.${signature}`,
    },
    custom: {
      subject: `Re: ${safeSubject}`,
      body: `${greeting}\n\nThank you for contacting Stingray.\n\n[Write your reply here]${signature}`,
    },
  };

  return templates[key];
}

function formatDate(value: string) {
  const parsed = Date.parse(value);
  if (Number.isNaN(parsed)) {
    return value;
  }

  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(parsed);
}

function ContactInboxContent() {
  const { theme } = useTheme();
  const dark = theme === 'dark';
  const [submissions, setSubmissions] = useState<ContactSubmissionRecord[]>([]);
  const [selectedId, setSelectedId] = useState('');
  const [filter, setFilter] = useState<'all' | ContactSubmissionStatus>('all');
  const [draftNotes, setDraftNotes] = useState('');
  const [templateKey, setTemplateKey] = useState<EmailTemplateKey>('received');
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [copiedEmail, setCopiedEmail] = useState(false);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const nextSubmissions = await fetchContactSubmissions();
        setSubmissions(nextSubmissions);
        setSelectedId((current) => current || nextSubmissions[0]?.id || '');
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Unable to load contact submissions.');
      }
    };

    void load();
  }, []);

  const filteredSubmissions = useMemo(() => {
    if (filter === 'all') {
      return submissions;
    }

    return submissions.filter((submission) => submission.status === filter);
  }, [filter, submissions]);

  const selectedSubmission = useMemo(() => {
    return submissions.find((submission) => submission.id === selectedId) ?? filteredSubmissions[0] ?? null;
  }, [filteredSubmissions, selectedId, submissions]);

  useEffect(() => {
    if (!selectedSubmission) {
      setDraftNotes('');
      return;
    }

    setDraftNotes(selectedSubmission.adminNotes);
  }, [selectedSubmission]);

  useEffect(() => {
    if (!selectedSubmission) {
      setEmailSubject('');
      setEmailBody('');
      return;
    }

    const template = buildEmailTemplate(templateKey, selectedSubmission);
    setEmailSubject(template.subject);
    setEmailBody(template.body);
  }, [selectedSubmission, templateKey]);

  useEffect(() => {
    if (!selectedSubmission && filteredSubmissions[0]) {
      setSelectedId(filteredSubmissions[0].id);
    }
  }, [filteredSubmissions, selectedSubmission]);

  const card = dark ? 'bg-[#1C2B3A] border border-white/5' : 'bg-white border border-gray-200';
  const text = dark ? 'text-white' : 'text-gray-900';
  const muted = dark ? 'text-white/50' : 'text-gray-500';
  const subtle = dark ? 'bg-white/5 border-white/10' : 'bg-gray-50 border-gray-200';
  const input = dark
    ? 'bg-[#0D1B2A] border-white/10 text-white placeholder-white/30'
    : 'bg-white border-gray-200 text-gray-900 placeholder-gray-400';

  const counts = {
    all: submissions.length,
    new: submissions.filter((submission) => submission.status === 'new').length,
    in_progress: submissions.filter((submission) => submission.status === 'in_progress').length,
    replied: submissions.filter((submission) => submission.status === 'replied').length,
    archived: submissions.filter((submission) => submission.status === 'archived').length,
  };

  const handleSelect = (submission: ContactSubmissionRecord) => {
    setSelectedId(submission.id);
    setDraftNotes(submission.adminNotes);
  };

  const handleStatusChange = async (status: ContactSubmissionStatus) => {
    if (!selectedSubmission) {
      return;
    }

    const previous = selectedSubmission.status;
    setSubmissions((current) => current.map((submission) => (
      submission.id === selectedSubmission.id
        ? { ...submission, status }
        : submission
    )));
    setSaving(true);

    try {
      const updated = await updateContactSubmission(selectedSubmission.id, { status });
      setSubmissions((current) => current.map((submission) => (
        submission.id === updated.id ? updated : submission
      )));
      setError('');
    } catch (err) {
      setSubmissions((current) => current.map((submission) => (
        submission.id === selectedSubmission.id
          ? { ...submission, status: previous }
          : submission
      )));
      setError(err instanceof Error ? err.message : 'Unable to update submission status.');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveNotes = async () => {
    if (!selectedSubmission) {
      return;
    }

    setSaving(true);
    try {
      const updated = await updateContactSubmission(selectedSubmission.id, { adminNotes: draftNotes });
      setSubmissions((current) => current.map((submission) => (
        submission.id === updated.id ? updated : submission
      )));
      setError('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to save admin notes.');
    } finally {
      setSaving(false);
    }
  };

  const handleCopyEmail = async () => {
    if (!emailSubject && !emailBody) {
      return;
    }

    await navigator.clipboard.writeText(`Subject: ${emailSubject}\n\n${emailBody}`);
    setCopiedEmail(true);
    setTimeout(() => setCopiedEmail(false), 2000);
  };

  return (
    <div className="space-y-5">
      {error && (
        <div className="text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg px-4 py-3">
          {error}
        </div>
      )}

      <div className={`${card} rounded-lg p-4`}>
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <p className={`text-xs font-black uppercase tracking-[0.22em] ${muted}`}>Inbox</p>
            <h2 className={`text-xl font-black mt-1 ${text}`}>Contact submissions</h2>
            <p className={`text-sm mt-1 ${muted}`}>Review inbound website messages, add admin notes, and track reply status.</p>
          </div>
          <div className="flex flex-wrap gap-2">
            {[
              { key: 'all', label: 'All' },
              { key: 'new', label: 'New' },
              { key: 'in_progress', label: 'In Progress' },
              { key: 'replied', label: 'Replied' },
              { key: 'archived', label: 'Archived' },
            ].map((item) => (
              <button
                key={item.key}
                onClick={() => setFilter(item.key as 'all' | ContactSubmissionStatus)}
                className={`px-4 py-1.5 rounded-full text-xs font-semibold transition-all cursor-pointer whitespace-nowrap ${
                  filter === item.key
                    ? 'text-white'
                    : dark
                    ? 'bg-white/5 text-white/60 hover:text-white hover:bg-white/10'
                    : 'bg-gray-100 text-gray-500 hover:text-gray-900 hover:bg-gray-200'
                }`}
                style={filter === item.key ? { backgroundColor: 'var(--accent)' } : {}}
              >
                {item.label}
                <span className="ml-1.5 opacity-70">
                  ({counts[item.key as keyof typeof counts]})
                </span>
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-[360px_minmax(0,1fr)] gap-5">
        <div className={`${card} rounded-lg overflow-hidden`}>
          <div className={`px-5 py-4 border-b ${dark ? 'border-white/10' : 'border-gray-100'}`}>
            <h3 className={`text-sm font-bold ${text}`}>Recent messages</h3>
          </div>
          <div className={`divide-y ${dark ? 'divide-white/5' : 'divide-gray-100'} max-h-[70vh] overflow-y-auto`}>
            {filteredSubmissions.length === 0 ? (
              <div className={`px-5 py-10 text-sm ${muted}`}>No contact submissions in this queue.</div>
            ) : (
              filteredSubmissions.map((submission) => {
                const meta = STATUS_META[submission.status];
                const active = selectedSubmission?.id === submission.id;

                return (
                  <button
                    key={submission.id}
                    onClick={() => handleSelect(submission)}
                    className={`w-full text-left px-5 py-4 transition-colors ${
                      active
                        ? dark
                          ? 'bg-white/10'
                          : 'bg-gray-50'
                        : dark
                        ? 'hover:bg-white/5'
                        : 'hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className={`text-sm font-semibold truncate ${text}`}>{submission.subject}</p>
                        <p className={`text-xs mt-1 ${muted}`}>{submission.firstName} {submission.lastName}</p>
                        <p className={`text-xs mt-0.5 truncate ${muted}`}>{submission.email}</p>
                      </div>
                      <span className={`text-[11px] font-semibold px-2.5 py-1 rounded-full shrink-0 ${meta.pill}`}>
                        {meta.label}
                      </span>
                    </div>
                    <p className={`text-xs mt-3 ${muted}`}>{submission.message.slice(0, 140)}{submission.message.length > 140 ? '...' : ''}</p>
                    <div className={`flex items-center justify-between text-[11px] mt-3 ${muted}`}>
                      <span>{formatDate(submission.createdAt)}</span>
                      <span>{submission.sourceUrl || 'Website contact form'}</span>
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </div>

        <div className={`${card} rounded-lg overflow-hidden`}>
          {!selectedSubmission ? (
            <div className={`px-6 py-16 text-center text-sm ${muted}`}>
              Select a contact submission to review the message.
            </div>
          ) : (
            <>
              <div className={`px-6 py-5 border-b ${dark ? 'border-white/10' : 'border-gray-100'}`}>
                <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className={`text-lg font-black ${text}`}>{selectedSubmission.subject}</h3>
                      <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${STATUS_META[selectedSubmission.status].pill}`}>
                        {STATUS_META[selectedSubmission.status].label}
                      </span>
                    </div>
                    <p className={`text-sm mt-2 ${muted}`}>
                      {selectedSubmission.firstName} {selectedSubmission.lastName} · {selectedSubmission.email}
                    </p>
                    <p className={`text-xs mt-1 ${muted}`}>
                      Received {formatDate(selectedSubmission.createdAt)}
                      {selectedSubmission.phone ? ` · ${selectedSubmission.phone}` : ''}
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <a
                      href={`mailto:${selectedSubmission.email}?subject=${encodeURIComponent(`Re: ${selectedSubmission.subject}`)}`}
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold text-white"
                      style={{ backgroundColor: 'var(--accent)' }}
                    >
                      <i className="ri-mail-send-line"></i>
                      Reply by Email
                    </a>
                    {selectedSubmission.sourceUrl && (
                      <a
                        href={selectedSubmission.sourceUrl}
                        target="_blank"
                        rel="noreferrer"
                        className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold border ${subtle}`}
                      >
                        <i className="ri-external-link-line"></i>
                        Source
                      </a>
                    )}
                  </div>
                </div>
              </div>

              <div className="p-6 space-y-6">
                <div className={`rounded-xl border p-4 ${subtle}`}>
                  <div className="flex items-center justify-between gap-3 mb-3">
                    <p className={`text-xs font-black uppercase tracking-[0.22em] ${muted}`}>Message</p>
                    <button
                      onClick={() => {
                        void navigator.clipboard.writeText(selectedSubmission.message);
                      }}
                      className={`text-xs font-semibold ${muted}`}
                    >
                      Copy text
                    </button>
                  </div>
                  <p className={`text-sm leading-7 whitespace-pre-wrap ${text}`}>{selectedSubmission.message}</p>
                </div>

                <div className={`rounded-xl border p-4 ${subtle}`}>
                  <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                    <div>
                      <p className={`text-xs font-black uppercase tracking-[0.22em] ${muted}`}>Reply Draft</p>
                      <h4 className={`text-base font-black mt-1 ${text}`}>Email templates</h4>
                      <p className={`text-sm mt-1 ${muted}`}>Start with a prepared response, then edit before sending.</p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {[
                        { key: 'received', label: 'Acknowledged' },
                        { key: 'follow_up', label: 'Follow-up' },
                        { key: 'dealer_interest', label: 'Dealer' },
                        { key: 'product_help', label: 'Product Help' },
                        { key: 'custom', label: 'Custom' },
                      ].map((template) => (
                        <button
                          key={template.key}
                          onClick={() => setTemplateKey(template.key as EmailTemplateKey)}
                          className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-all cursor-pointer whitespace-nowrap ${
                            templateKey === template.key
                              ? 'text-white'
                              : dark
                              ? 'bg-white/5 text-white/60 hover:text-white hover:bg-white/10'
                              : 'bg-white text-gray-500 hover:text-gray-900 hover:bg-gray-100'
                          }`}
                          style={templateKey === template.key ? { backgroundColor: 'var(--accent)' } : {}}
                        >
                          {template.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="mt-4 space-y-3">
                    <div>
                      <p className={`text-xs font-black uppercase tracking-[0.22em] mb-2 ${muted}`}>Subject</p>
                      <input
                        type="text"
                        value={emailSubject}
                        onChange={(event) => setEmailSubject(event.target.value)}
                        className={`w-full rounded-xl border px-4 py-3 text-sm outline-none ${input}`}
                      />
                    </div>
                    <div>
                      <p className={`text-xs font-black uppercase tracking-[0.22em] mb-2 ${muted}`}>Email Body</p>
                      <textarea
                        value={emailBody}
                        onChange={(event) => setEmailBody(event.target.value)}
                        className={`w-full min-h-[220px] rounded-xl border px-4 py-3 text-sm outline-none resize-y ${input}`}
                      />
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <a
                        href={`mailto:${selectedSubmission.email}?subject=${encodeURIComponent(emailSubject)}&body=${encodeURIComponent(emailBody)}`}
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold text-white"
                        style={{ backgroundColor: 'var(--accent)' }}
                      >
                        <i className="ri-mail-open-line"></i>
                        Open in Mail App
                      </a>
                      <button
                        onClick={() => void handleCopyEmail()}
                        className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold border ${subtle}`}
                      >
                        <i className={copiedEmail ? 'ri-check-line' : 'ri-clipboard-line'}></i>
                        {copiedEmail ? 'Copied' : 'Copy Draft'}
                      </button>
                      <button
                        onClick={() => void handleStatusChange('replied')}
                        disabled={saving}
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold text-white disabled:opacity-60"
                        style={{ backgroundColor: '#10B981' }}
                      >
                        <i className="ri-mail-check-line"></i>
                        Mark Replied
                      </button>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-[220px_minmax(0,1fr)] gap-5">
                  <div className={`rounded-xl border p-4 ${subtle}`}>
                    <p className={`text-xs font-black uppercase tracking-[0.22em] mb-3 ${muted}`}>Workflow</p>
                    <div className="space-y-2">
                      {(Object.keys(STATUS_META) as ContactSubmissionStatus[]).map((status) => (
                        <button
                          key={status}
                          onClick={() => void handleStatusChange(status)}
                          disabled={saving}
                          className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold transition-colors ${
                            selectedSubmission.status === status
                              ? STATUS_META[status].pill
                              : dark
                              ? 'bg-white/5 text-white/70 hover:bg-white/10'
                              : 'bg-white text-gray-600 hover:bg-gray-100'
                          }`}
                        >
                          <i className={STATUS_META[status].icon}></i>
                          {STATUS_META[status].label}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className={`rounded-xl border p-4 ${subtle}`}>
                    <div className="flex items-center justify-between gap-3 mb-3">
                      <p className={`text-xs font-black uppercase tracking-[0.22em] ${muted}`}>Admin Notes</p>
                      <button
                        onClick={() => void handleSaveNotes()}
                        disabled={saving}
                        className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-bold text-white disabled:opacity-60"
                        style={{ backgroundColor: 'var(--accent)' }}
                      >
                        <i className="ri-save-line"></i>
                        Save Notes
                      </button>
                    </div>
                    <textarea
                      value={draftNotes}
                      onChange={(event) => setDraftNotes(event.target.value)}
                      placeholder="Add internal notes, reply summary, or follow-up details..."
                      className={`w-full min-h-[220px] rounded-xl border px-4 py-3 text-sm outline-none resize-y ${input}`}
                    />
                    <p className={`text-xs mt-3 ${muted}`}>Last updated {formatDate(selectedSubmission.updatedAt)}</p>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default function AdminContactSubmissionsPage() {
  return (
    <AdminLayout title="Contact Inbox">
      <ContactInboxContent />
    </AdminLayout>
  );
}
